import { useEffect, useRef, useState, type MouseEvent, type PointerEvent } from 'react';
import { Link, NavLink, useLocation } from 'react-router';
import { Marca } from './Marca.js';
import { useTema, type Tema } from '../hooks/useTema.js';
import { useSesion } from '../hooks/useSesion.js';
import { AUTH_DISPONIBLE } from '../services/auth.js';
import { obtenerPerfil } from '../services/cuentas.js';
import { diccionarioDe, type Idioma } from '../../i18n/index.js';

const TEXTOS = {
  es: {
    fuentes: 'Fuentes',
    comoFunciona: 'Cómo funciona',
    quien: 'Quién está detrás',
    autores: 'Autores y editores',
    temaClaro: 'Tema claro',
    temaOscuro: 'Tema oscuro',
    navegacion: 'Principal',
    sinapser: 'Sinapser',
    deSinapser: 'de Sinapser',
    menu: 'Menú',
    cerrarMenu: 'Cerrar menú',
    admin: 'Admin',
  },
  en: {
    fuentes: 'Sources',
    comoFunciona: 'How it works',
    quien: 'Who is behind this',
    autores: 'Authors & publishers',
    temaClaro: 'Light theme',
    temaOscuro: 'Dark theme',
    navegacion: 'Main',
    sinapser: 'Sinapser',
    deSinapser: 'by Sinapser',
    menu: 'Menu',
    cerrarMenu: 'Close menu',
    admin: 'Admin',
  },
} as const;

/** Enlaces a la web de Sinapser y a sus productos. Cada idioma va a su versión de sinapser.com (`/en/...` en inglés). */
const ENLACES_SINAPSER = {
  es: [
    { nombre: 'Sinapser', href: 'https://sinapser.com/' },
    { nombre: 'EVA', href: 'https://sinapser.com/eva' },
    { nombre: 'EVA Brain', href: 'https://sinapser.com/eva-brain' },
  ],
  en: [
    { nombre: 'Sinapser', href: 'https://sinapser.com/en' },
    { nombre: 'EVA', href: 'https://sinapser.com/en/eva' },
    { nombre: 'EVA Brain', href: 'https://sinapser.com/en/eva-brain' },
  ],
} as const;

/** El botón de idioma dice el idioma al que se cambia, escrito en ese idioma: así lo encuentra quien no lee el otro. */
const OTRO_IDIOMA = { es: { idioma: 'en', nombre: 'English' }, en: { idioma: 'es', nombre: 'Español' } } as const;

/** A partir de este ancho (el `xl` de Tailwind, en `rem` igual que él) la cabecera cabe en una línea; por debajo, sus enlaces van al menú lateral. */
const ANCHO_CABECERA_COMPLETA = '(min-width: 80rem)';

/**
 * Cabecera de todas las pantallas (plan/design/Main.dc.html). Se pinta una sola vez, en el marco de
 * las rutas (`routes/rutas.tsx`), y no en cada página: seis copias acababan diciendo cosas distintas.
 *
 * En pantallas estrechas los enlaces y los controles no caben en una línea: se sacan a un menú lateral
 * (`<dialog>` modal, que ya trae el foco atrapado, el Escape y el fondo inerte). Los tres bloques se
 * pintan dos veces, uno en la cabecera y otro en el menú, y solo se ve uno según el ancho.
 */
export function Cabecera({ idioma, onCambiarIdioma }: { readonly idioma: Idioma; readonly onCambiarIdioma: (idioma: Idioma) => void }) {
  const { tema, alternar } = useTema();
  const t = TEXTOS[idioma];
  const menu = useRef<HTMLDialogElement>(null);
  const [abierto, setAbierto] = useState(false);
  const { pathname } = useLocation();

  // Al cambiar de pantalla (también con el botón de atrás del navegador) el menú no se queda abierto encima.
  useEffect(() => {
    menu.current?.close();
  }, [pathname]);

  // Un `<dialog>` modal sigue volviendo inerte la página aunque el CSS lo oculte: si se ensancha la ventana, se cierra.
  useEffect(() => {
    const ancho = window.matchMedia(ANCHO_CABECERA_COMPLETA);
    const alCambiar = () => {
      if (ancho.matches) menu.current?.close();
    };
    ancho.addEventListener('change', alCambiar);
    return () => {
      ancho.removeEventListener('change', alCambiar);
    };
  }, []);

  /**
   * Cierra al pulsar el fondo (el propio `<dialog>`, porque el contenido ocupa toda la hoja) o cualquier enlace.
   * Con el fondo hace falta que la pulsación también haya EMPEZADO en él: al arrastrar desde dentro (seleccionar
   * texto del menú, por ejemplo) y soltar fuera, el navegador manda el `click` al `<dialog>`, su ancestro común.
   */
  const pulsacionEmpezoEnElFondo = useRef(false);
  const alEmpezarPulsacion = (e: PointerEvent<HTMLDialogElement>) => {
    pulsacionEmpezoEnElFondo.current = e.target === e.currentTarget;
  };
  const alPulsarMenu = (e: MouseEvent<HTMLDialogElement>) => {
    const enFondo = e.target === e.currentTarget && pulsacionEmpezoEnElFondo.current;
    if (enFondo || (e.target instanceof Element && e.target.closest('a'))) e.currentTarget.close();
  };

  return (
    <header className='flex min-h-[72px] min-w-0 items-center gap-x-4 px-4 py-3 sm:px-10'>
      <Link to='/' className='mr-auto inline-flex items-center gap-2.5 text-[18px] font-bold tracking-tight text-(--ink)'>
        <Marca />
        <span>Athena</span>
        <span className='text-[13px] font-medium text-(--ink-faint)'>{t.deSinapser}</span>
      </Link>

      <EnlacesPrincipales idioma={idioma} />
      <EnlacesSinapser idioma={idioma} />
      <Controles idioma={idioma} onCambiarIdioma={onCambiarIdioma} tema={tema} alternar={alternar} />

      <button
        type='button'
        aria-label={t.menu}
        aria-haspopup='dialog'
        aria-expanded={abierto}
        onClick={() => {
          menu.current?.showModal();
          setAbierto(true);
        }}
        className='-mr-2 grid h-11 w-11 place-items-center rounded-md text-(--ink) xl:hidden'
      >
        <Icono trazo='M4 7h16M4 12h16M4 17h16' />
      </button>

      <dialog
        ref={menu}
        aria-label={t.menu}
        onClose={() => {
          setAbierto(false);
        }}
        onPointerDown={alEmpezarPulsacion}
        onClick={alPulsarMenu}
        className='menu-lateral fixed inset-y-0 right-0 left-auto m-0 h-dvh max-h-none w-[min(20rem,85vw)] max-w-none border-l border-(--line) bg-(--paper) p-0 text-(--ink) backdrop:bg-black/50 xl:hidden'
      >
        <div className='flex h-full flex-col gap-4 overflow-y-auto px-6 py-4'>
          <button
            type='button'
            aria-label={t.cerrarMenu}
            onClick={() => menu.current?.close()}
            className='-mr-2 grid h-11 w-11 shrink-0 place-items-center self-end rounded-md text-(--ink-faint) hover:text-(--ink)'
          >
            <Icono trazo='M6 6l12 12M18 6L6 18' />
          </button>
          <EnlacesPrincipales idioma={idioma} enMenu />
          <div className='border-t border-(--line) pt-2'>
            <EnlacesSinapser idioma={idioma} enMenu />
          </div>
          <Controles idioma={idioma} onCambiarIdioma={onCambiarIdioma} tema={tema} alternar={alternar} enMenu />
        </div>
      </dialog>
    </header>
  );
}

function Icono({ trazo }: { readonly trazo: string }) {
  return (
    <svg aria-hidden='true' viewBox='0 0 24 24' className='h-6 w-6' fill='none' stroke='currentColor' strokeWidth='1.75' strokeLinecap='round'>
      <path d={trazo} />
    </svg>
  );
}

/** Clases del contenedor de cada bloque: en la cabecera solo se ve de `xl` en adelante; en el menú, en columna y más grande. */
const claseBloque = (enMenu: boolean) =>
  enMenu
    ? 'flex flex-col text-[17px] font-medium text-(--ink-faint)'
    : 'hidden items-center gap-x-[22px] text-[14.5px] font-medium whitespace-nowrap text-(--ink-faint) xl:flex';

/** Cada enlace del menú ocupa una fila entera y alta, para que se pulse bien con el dedo. */
const claseEnlace = (enMenu: boolean) => (enMenu ? 'block py-3' : '');

function EnlacesPrincipales({ idioma, enMenu = false }: { readonly idioma: Idioma; readonly enMenu?: boolean }) {
  const t = TEXTOS[idioma];
  const enlace = ({ isActive }: { isActive: boolean }) => `${claseEnlace(enMenu)} ${isActive ? 'text-(--ink)' : 'hover:text-(--ink)'}`;
  return (
    <nav aria-label={t.navegacion} className={claseBloque(enMenu)}>
      <NavLink to='/fuentes' className={enlace}>
        {t.fuentes}
      </NavLink>
      <NavLink to='/como-funciona' className={enlace}>
        {t.comoFunciona}
      </NavLink>
      <NavLink to='/quien-esta-detras' className={enlace}>
        {t.quien}
      </NavLink>
      <NavLink to='/autores-y-editores' className={enlace}>
        {t.autores}
      </NavLink>
    </nav>
  );
}

/**
 * Sinapser, EVA y EVA Brain. En el menú lateral, uno debajo de otro. En la cabecera, plegados en un
 * desplegable «Sinapser ▾» (Iván, 2026-10-02): sueltos, la cabecera no cabía a 1371 px y el último
 * botón salía cortado.
 */
function EnlacesSinapser({ idioma, enMenu = false }: { readonly idioma: Idioma; readonly enMenu?: boolean }) {
  const [abierto, setAbierto] = useState(false);
  const caja = useRef<HTMLElement>(null);

  // Se cierra al pulsar fuera o con Escape, como cualquier desplegable.
  useEffect(() => {
    if (!abierto) return;
    const fuera = (e: globalThis.PointerEvent) => {
      if (caja.current !== null && !caja.current.contains(e.target as Node)) setAbierto(false);
    };
    const escape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAbierto(false);
    };
    document.addEventListener('pointerdown', fuera);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', fuera);
      document.removeEventListener('keydown', escape);
    };
  }, [abierto]);

  const enlaces = ENLACES_SINAPSER[idioma].map(({ nombre, href }) => (
    <a
      key={href}
      href={href}
      target='_blank'
      rel='noreferrer noopener'
      onClick={() => {
        setAbierto(false);
      }}
      className={
        enMenu
          ? `${claseEnlace(true)} hover:text-(--ink)`
          : 'block rounded px-3 py-2 whitespace-nowrap text-(--ink-soft) hover:bg-(--chat-bg) hover:text-(--ink)'
      }
    >
      {nombre}
    </a>
  ));

  if (enMenu)
    return (
      <nav aria-label={TEXTOS[idioma].sinapser} className={claseBloque(true)}>
        {enlaces}
      </nav>
    );

  return (
    <nav ref={caja} aria-label={TEXTOS[idioma].sinapser} className={`${claseBloque(false)} relative xl:border-l xl:border-(--line) xl:pl-[22px]`}>
      <button
        type='button'
        aria-expanded={abierto}
        onClick={() => {
          setAbierto(a => !a);
        }}
        className='inline-flex cursor-pointer items-center gap-1 border-none bg-transparent p-0 font-medium text-(--ink-faint) hover:text-(--ink)'
      >
        {TEXTOS[idioma].sinapser}
        <svg
          aria-hidden='true'
          viewBox='0 0 24 24'
          className={`h-4 w-4 transition-transform ${abierto ? 'rotate-180' : ''}`}
          fill='none'
          stroke='currentColor'
          strokeWidth='2'
          strokeLinecap='round'
        >
          <path d='M6 9l6 6 6-6' />
        </svg>
      </button>
      {abierto ? (
        <div className='absolute top-full right-0 z-20 mt-2 flex min-w-[11rem] flex-col rounded-md border border-(--line) bg-(--paper) p-1 shadow-lg'>
          {enlaces}
        </div>
      ) : null}
    </nav>
  );
}

function Controles({
  idioma,
  onCambiarIdioma,
  tema,
  alternar,
  enMenu = false,
}: {
  readonly idioma: Idioma;
  readonly onCambiarIdioma: (idioma: Idioma) => void;
  readonly tema: Tema;
  readonly alternar: () => void;
  readonly enMenu?: boolean;
}) {
  const t = TEXTOS[idioma];
  const tc = diccionarioDe(idioma).cuentas;
  const otro = OTRO_IDIOMA[idioma];
  const boton = `inline-flex items-center rounded-md border border-(--line) whitespace-nowrap text-(--ink-faint) hover:text-(--ink) ${enMenu ? 'px-3 py-2 text-[14px]' : 'h-[28px] px-2.5 text-[12px]'}`;
  const sesion = useSesion();

  // La autorización pertenece a una identidad concreta. Al cambiar de cuenta, el enlace
  // anterior se oculta durante la nueva consulta y sus respuestas tardías se descartan.
  const idUsuario = sesion?.user.id;
  const token = sesion?.access_token;
  const [accesoAdmin, setAccesoAdmin] = useState<{ idUsuario: string; token: string } | null>(null);
  useEffect(() => {
    if (idUsuario === undefined || token === undefined) {
      setAccesoAdmin(null);
      return;
    }
    let vigente = true;
    void obtenerPerfil(token).then(perfil => {
      if (vigente) setAccesoAdmin(perfil?.esAdmin ? { idUsuario, token } : null);
    });
    return () => {
      vigente = false;
    };
  }, [idUsuario, token]);
  const esAdmin = accesoAdmin !== null && accesoAdmin.idUsuario === idUsuario && accesoAdmin.token === token;

  return (
    <div className={enMenu ? 'mt-auto flex items-center gap-2 pt-2' : 'hidden items-center gap-2 xl:flex'}>
      {/* En la cabecera, compactos (Iván, 2026-10-02): «EN» y un icono, con su nombre entero para el lector de pantalla. En el menú, con texto. */}
      <button
        type='button'
        lang={otro.idioma}
        aria-label={otro.nombre}
        title={otro.nombre}
        onClick={() => {
          onCambiarIdioma(otro.idioma);
        }}
        className={boton}
      >
        {enMenu ? otro.nombre : otro.idioma.toUpperCase()}
      </button>
      <button
        type='button'
        onClick={alternar}
        aria-label={tema === 'dark' ? t.temaClaro : t.temaOscuro}
        title={tema === 'dark' ? t.temaClaro : t.temaOscuro}
        className={boton}
      >
        {enMenu ? (
          tema === 'dark' ? (
            t.temaClaro
          ) : (
            t.temaOscuro
          )
        ) : (
          <svg
            aria-hidden='true'
            viewBox='0 0 24 24'
            className='h-[15px] w-[15px]'
            fill='none'
            stroke='currentColor'
            strokeWidth='1.75'
            strokeLinecap='round'
            strokeLinejoin='round'
          >
            {tema === 'dark' ? (
              <path d='M12 4V2m0 20v-2m8-8h2M2 12h2m13.66-5.66 1.41-1.41M4.93 19.07l1.41-1.41m0-11.32L4.93 4.93m14.14 14.14-1.41-1.41M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10z' />
            ) : (
              <path d='M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z' />
            )}
          </svg>
        )}
      </button>
      {/*
        Sin AUTH_DISPONIBLE (faltan las variables de build de Supabase), no hay enlace: mostrar uno
        que lleva a un login roto es peor que no mostrar nada. `undefined` mientras se comprueba la
        sesión: no se ve un enlace que cambia justo después de pintar.
      */}
      {!AUTH_DISPONIBLE || sesion === undefined ? null : (
        <Link to={sesion === null ? '/iniciar-sesion' : '/mi-cuenta'} className={boton}>
          {sesion === null ? tc.iniciarSesion : tc.cuenta}
        </Link>
      )}
      {esAdmin ? (
        <Link to='/admin' className={boton}>
          {t.admin}
        </Link>
      ) : null}
    </div>
  );
}
